import { DocumentCategory } from 'src/app/services/documents/documents';

export enum IntentType {
  FORM_STATUS = 'FORM_STATUS',
  MY_APPROVALS = 'MY_APPROVALS',
  MY_ACTIONS = 'MY_ACTIONS',
  MY_SIGNATURES = 'MY_SIGNATURES',
  MY_SIGNATURE_STATUS = 'MY_SIGNATURE_STATUS',
  BLOCKING_INFO = 'BLOCKING_INFO',
  LIST_FORMS = 'LIST_FORMS',
  AFFECTED_SYSTEMS = 'AFFECTED_SYSTEMS',
  RESOURCE_STATUS = 'RESOURCE_STATUS',
  NAVIGATE = 'NAVIGATE',
  PERMISSION_INFO = 'PERMISSION_INFO',
  CAPABILITY_SUMMARY = 'CAPABILITY_SUMMARY',
  TRAINING_STATUS = 'TRAINING_STATUS',
  SCHEDULE_STATUS = 'SCHEDULE_STATUS',
  REVIEW_REQUESTS = 'REVIEW_REQUESTS',
  SEARCH = 'SEARCH',
  PRINT = 'PRINT',
  HELP = 'HELP',
  UNKNOWN = 'UNKNOWN',
}

export interface ParsedIntent {
  type: IntentType;
  serialNo?: string;         // e.g. "SCF-24-001", "RSSWA-24-001"
  formType?: string;         // "SCF" | "RSSWA" | "EB" | "PPSTB" | "RSF" | "ABSI" | "KE"
  documentTypeQuery?: string;
  isChecklist?: boolean;     // true when querying checklists rather than forms
  statusFilter?: string;     // "Active" | "Draft" | "Closed" | etc.
  resourceName?: string;     // e.g. "BL 5.0.2", "ALS", "1.4"
  resourceKind?: 'beamline' | 'branchline' | 'accelerator';
  impactDirection?: 'documentsByResource' | 'resourcesByDocument';
  action?: 'create';
  approvalScope?: 'all' | 'catalogs' | 'templates';
  signatureScope?: 'all' | 'forms' | 'checklists';
  trainingScope?: 'missing' | 'pendingApproval' | 'approval';
  scheduleScope?: 'expired' | 'expiringNextWeek' | 'resourceReview' | 'resourceSchedules';
  scheduleTypeQuery?: string;
  reviewBoard?: 'BRC' | 'ARC';
  reviewScope?: 'pending' | 'inProgress';
  searchQuery?: string;
  countOnly?: boolean;
  listSort?: 'recent';
  rawText: string;
}

export interface WorkflowAssistantInterpretResponse {
  available?: boolean;
  interpreted: boolean;
  intent?: ParsedIntent;
}

export enum WorkflowAssistantSuggestionStatus {
  New = 1,
  UnderReview = 2,
  Accepted = 3,
  Implemented = 4,
  Rejected = 5,
}

export enum WorkflowAssistantSubmissionType {
  QuestionSuggestion = 1,
  IncorrectAnswer = 2,
}

export interface WorkflowAssistantSuggestion {
  id: number;
  question: string;
  expectedBehavior: string;
  exampleReference?: string;
  submissionType: WorkflowAssistantSubmissionType;
  assistantAnswer?: string;
  status: WorkflowAssistantSuggestionStatus;
  developerNotes?: string;
  createdOn: string;
  createdByID: number;
  createdByName: string;
  updatedOn?: string;
  updatedByID?: number;
  updatedByName?: string;
  implementedOn?: string;
  implementedByID?: number;
  implementedByName?: string;
}

export interface CreateWorkflowAssistantSuggestion {
  question: string;
  expectedBehavior: string;
  exampleReference?: string;
}

export interface UpdateWorkflowAssistantSuggestion {
  status: WorkflowAssistantSuggestionStatus;
  developerNotes?: string;
}

export interface CreateWorkflowAssistantIncorrectAnswerReport {
  question: string;
  assistantAnswer: string;
  expectedBehavior: string;
}

export interface ChatMessage {
  id: string;
  sender: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  links?: ChatLink[];
  action?: 'search' | 'print';
  actionValue?: string;
  isError?: boolean;
  isLoading?: boolean;
  sourceQuestion?: string;
  feedbackSubmitted?: boolean;
}

export interface ChatLink {
  label: string;
  route: string;
}

export interface WorkflowPendingSignature {
  wfTableID: number;
  wfTaskID: number;
  wfTaskLocalID: number;
  formID?: number;
  serialNo: string;
  documentCategory?: DocumentCategory;
  formType: number;
  formTypeName: string;
  documentTypeName?: string;
  statusName?: string;
  sectionNumber?: number;
  sectionName?: string;
  stepName?: string;
  stepOrder?: number;
  taskName: string;
  taskOrder?: number;
  actionName?: string;
  roleNames?: string[];
  userNames?: string[];
  route: string;
}

export const FORM_SERIAL_PATTERN =
  /\b(SCF|RSSWA|EBF?|PPSTB|RSF|ABSI|KE|RCP)[-\s][\w-]+/i;

export const FORM_TYPE_WORDS: Record<string, string> = {
  scf: 'SCF',
  scfs: 'SCF',
  'safety control': 'SCF',
  'shielding control': 'SCF',
  rsswa: 'RSSWA',
  rsswas: 'RSSWA',
  rss: 'RSSWA',
  rsss: 'RSSWA',
  'work auth': 'RSSWA',
  'work authorization': 'RSSWA',
  eb: 'EB',
  ebs: 'EB',
  ebf: 'EB',
  ebfs: 'EB',
  bypass: 'EB',
  ppstb: 'PPSTB',
  ppstbs: 'PPSTB',
  maintenance: 'PPSTB',
  rsf: 'RSF',
  rsfs: 'RSF',
  radiation: 'RSF',
  'rad survey': 'RSF',
  absi: 'ABSI',
  absis: 'ABSI',
  startup: 'ABSI',
  'start up': 'ABSI',
  'start-up': 'ABSI',
  kenable: 'KE',
  kenables: 'KE',
  ke: 'KE',
  kes: 'KE',
  'key enable': 'KE',
  'k enable': 'KE',
};

export const STATUS_WORDS: Record<string, string> = {
  active: 'Active',
  open: 'Open',
  opened: 'Open',
  draft: 'Draft',
  closed: 'Closed',
  close: 'Closed',
  pending: 'WorkApprovals',
  'work approvals': 'WorkApprovals',
  'pending activation': 'PendingActivation',
  'awaiting inspection': 'AwaitingInspection',
  'hold point': 'HoldPoint',
  'holdpoint': 'HoldPoint',
  amendment: 'Amendment',
  canceled: 'Canceled',
  cancelled: 'Canceled',
  completed: 'WorkCompleted',
  'work completed': 'WorkCompleted',
  'pending review': 'PendingReview',
  reviewing: 'Reviewing',
  correcting: 'Correcting',
  'approval pending': 'ApprovalPending',
  approvals: 'Approvals',
  preparation: 'Preparation',
  'close out': 'CloseOut',
  'review and concurrence': 'ReviewAndConcurrence',
  testing: 'Testing',
  'shielding inspections': 'ShieldingInspections',
  'pps testing': 'PpsTesting',
  'pps testing complete': 'PpsTestingComplete',
  'pending requester concurrence': 'PendingRequesterConcurrence',
  'pending safety controls': 'PendingSafetyControls',
};
